// FOR-IN cursor-record binding extractor — Oracle PL/SQL and PL/pgSQL.
//
// Scans the buffer for `FOR <rec> IN <cursor-or-inline-select> LOOP` patterns
// that appear BEFORE the cursor, and returns a map from the (lowercased)
// record name to the SELECT-list columns the record exposes. Used by
// `emitDottedAccess` in `oracleResolver.ts` so that `<rec>.|<col>` surfaces
// only the cursor's projected columns (matches the PL/SQL runtime, which
// REJECTS access to non-projected columns).
//
// Pinned by oracle script.33.§8 (inline FOR-IN, projection `log_id,
// operation, changed_at`) and script.37.§4 (named-cursor FOR-IN, projection
// from `CURSOR c_employees IS SELECT e.employee_id, e.first_name, e.salary
// FROM …`).
//
// Numeric-range FOR (`FOR i IN 1..10 LOOP`) is intentionally skipped — `i`
// is a scalar, not a record, so dotted access on it has no projection.
//
// Pure text-walk like `findOracleProceduralRowtypeBindings`. Allocation-light
// and avoids touching the grammar bundle on the dotted-access fast path.

import type { DatabaseEngineId } from '@/core/engines';

import { tokenize, type Token } from '../context/tokenizer';

export type OracleForInBinding = {
  /** Lowercased projected column names. */
  readonly columns: readonly string[];
};

const upperOf = (t: Token | undefined): string | null => {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
};

/**
 * Walk the buffer up to `cursorOffset` and return every `FOR <ident> IN …
 * LOOP` binding whose projection we can statically infer. Two shapes:
 *
 *   1. Inline: `FOR <rec> IN ( SELECT <list> FROM … ) LOOP`
 *      — the parenthesized SELECT's projection list is parsed directly.
 *   2. Named: `FOR <rec> IN <cursorName> LOOP`
 *      — we scan upstream for the matching `CURSOR <cursorName> IS SELECT
 *      <list> FROM …` and reuse its projection.
 *
 * Both shapes resolve to a `string[]` of column names (lowercased). The
 * extractor tolerates AS aliases (`expr AS alias` projects `alias`), bare
 * qualified columns (`t.col` projects `col`), and plain unqualified columns.
 * Other projection shapes (function calls without AS, `*`, sub-selects in
 * the projection) currently yield no entry for that column — the caller's
 * downstream UX is acceptable (some columns surface, none leak from a wrong
 * table), and the spec scripts that drive this work use only simple shapes.
 */
export function findOracleProceduralForInBindings(
  source: string,
  cursorOffset: number,
): Map<string, OracleForInBinding> {
  return findProceduralForInBindings(source, cursorOffset, 'oracle');
}

/**
 * PL/pgSQL spells the same loop WITHOUT the parentheses Oracle requires:
 *
 *     FOR r_row IN
 *       SELECT r.rental_id, r.rental_date
 *       FROM rental r
 *     LOOP
 *
 * so `r_row.▮` needs the projection of a bare SELECT that runs until the
 * `LOOP` keyword (shape C below). Everything else — the record name, the
 * projection parser, the numeric-range skip — is identical, so this is the
 * same walk with one extra shape rather than a second module.
 * Closes pgsql script.37 §7.
 */
export function findProceduralForInBindings(
  source: string,
  cursorOffset: number,
  engine: DatabaseEngineId,
): Map<string, OracleForInBinding> {
  const bindings = new Map<string, OracleForInBinding>();
  if (cursorOffset <= 0) return bindings;

  const tokens = tokenize(source, 0, source.length, engine);
  const visible: Token[] = [];
  for (const t of tokens) {
    if (t.start >= cursorOffset) break;
    visible.push(t);
  }

  // Pre-collect named-cursor declarations once: scan whole visible token
  // window for `CURSOR <name> IS SELECT <list> FROM …`. Map by lowercased
  // cursor name. The expression `<list>` ends at the matching `FROM` at
  // depth 0.
  const cursorProjections = collectNamedCursorProjections(visible);

  for (let i = 0; i + 3 < visible.length; i += 1) {
    if (upperOf(visible[i]) !== 'FOR') continue;
    const rec = visible[i + 1];
    if (!rec || rec.type !== 'ident') continue;
    if (upperOf(visible[i + 2]) !== 'IN') continue;

    const after = visible[i + 3]!;

    // Shape A: inline parenthesized SELECT.
    if (after.type === 'lparen') {
      const closeIdx = findMatchingRparen(visible, i + 3);
      if (closeIdx === -1) continue;
      const cols = collectSelectListColumns(visible, i + 4, closeIdx);
      if (cols.length > 0) {
        bindings.set(rec.text.toLowerCase(), { columns: cols });
      }
      i = closeIdx;
      continue;
    }

    // Shape C: PL/pgSQL's unparenthesized query — `FOR <rec> IN SELECT <list>
    // FROM … LOOP`. Oracle requires the parens of shape A, so this is gated to
    // the engines that don't: admitting it for Oracle would read a `FOR r IN
    // <cursor>` whose name happened to be `select` as a query.
    if (engine !== 'oracle' && upperOf(after) === 'SELECT') {
      const fromIdx = findClauseKeywordAtTopLevel(visible, i + 4, ['FROM', 'LOOP']);
      if (fromIdx !== -1) {
        const cols = collectSelectListColumns(visible, i + 4, fromIdx);
        if (cols.length > 0) {
          bindings.set(rec.text.toLowerCase(), { columns: cols });
        }
        i = fromIdx;
        continue;
      }
    }

    // Shape B: named cursor — `FOR <rec> IN <cursorName> LOOP`. Skip
    // numeric-range FOR (next token is a number literal) by checking that
    // `after` is an identifier.
    if (after.type === 'ident') {
      const cursorName = after.text.toLowerCase();
      const cols = cursorProjections.get(cursorName);
      if (cols && cols.length > 0) {
        bindings.set(rec.text.toLowerCase(), { columns: cols });
      }
      i += 3;
      continue;
    }
  }

  return bindings;
}

/**
 * Index of the first of `words` that appears at paren depth 0 from `start`,
 * or -1. Stops at a `;` — a projection never spans a statement boundary.
 */
function findClauseKeywordAtTopLevel(
  tokens: readonly Token[],
  start: number,
  words: readonly string[],
): number {
  let depth = 0;
  for (let i = start; i < tokens.length; i += 1) {
    const t = tokens[i]!;
    if (t.type === 'lparen') depth += 1;
    else if (t.type === 'rparen') depth -= 1;
    else if (depth === 0 && t.type === 'semicolon') return -1;
    else if (depth === 0) {
      const up = upperOf(t);
      if (up && words.includes(up)) return i;
    }
  }
  return -1;
}

function collectNamedCursorProjections(tokens: readonly Token[]): Map<string, readonly string[]> {
  const out = new Map<string, readonly string[]>();
  for (let i = 0; i + 4 < tokens.length; i += 1) {
    if (upperOf(tokens[i]) !== 'CURSOR') continue;
    const name = tokens[i + 1];
    if (!name || name.type !== 'ident') continue;
    if (upperOf(tokens[i + 2]) !== 'IS') continue;
    if (upperOf(tokens[i + 3]) !== 'SELECT') continue;

    // Find FROM at depth 0 within this CURSOR's IS clause.
    let depth = 0;
    let fromIdx = -1;
    for (let j = i + 4; j < tokens.length; j += 1) {
      const t = tokens[j]!;
      if (t.type === 'lparen') depth += 1;
      else if (t.type === 'rparen') depth -= 1;
      else if (depth === 0 && upperOf(t) === 'FROM') {
        fromIdx = j;
        break;
      } else if (depth === 0 && t.type === 'semicolon') {
        break;
      }
    }
    if (fromIdx === -1) continue;

    const cols = collectSelectListColumns(tokens, i + 4, fromIdx);
    if (cols.length > 0) {
      out.set(name.text.toLowerCase(), cols);
    }
    i = fromIdx;
  }
  return out;
}

function findMatchingRparen(tokens: readonly Token[], lparenIdx: number): number {
  let depth = 0;
  for (let i = lparenIdx; i < tokens.length; i += 1) {
    const t = tokens[i]!;
    if (t.type === 'lparen') depth += 1;
    else if (t.type === 'rparen') {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

/**
 * Parse the SELECT projection list from [start, endExclusive). The caller
 * provides bounds: for inline FOR-IN this is `(<lparen>+1, <rparen>)`; for
 * named cursors this is `(<select-keyword>+1, <from-keyword>)`. The window
 * may start with the `SELECT` token itself (we skip it).
 *
 * Comma splits at depth 0 separate columns. For each column expression we
 * inspect the LAST two/three tokens before the comma:
 *   - `... AS <alias>`  → alias
 *   - `... <ident> . <colName>` → colName (qualified bare projection)
 *   - `<colName>`  → colName (unqualified bare projection)
 *   - anything else (function call without AS, *, sub-selects) → skipped
 */
function collectSelectListColumns(
  tokens: readonly Token[],
  start: number,
  endExclusive: number,
): string[] {
  let i = start;
  while (i < endExclusive && upperOf(tokens[i]) === 'SELECT') i += 1;
  if (i >= endExclusive) return [];

  // Cap the scan at FROM at depth 0 — past that point we're in the FROM /
  // WHERE / ORDER BY clause, not the projection list. Callers that already
  // pre-clip (collectNamedCursorProjections passes endExclusive=FROM) hit
  // the no-op fast path; inline FOR-IN callers pass endExclusive=rparen
  // and rely on this to find the inner FROM.
  let fromIdx = endExclusive;
  let scanDepth = 0;
  for (let j = i; j < endExclusive; j += 1) {
    const t = tokens[j]!;
    if (t.type === 'lparen') scanDepth += 1;
    else if (t.type === 'rparen') scanDepth -= 1;
    else if (scanDepth === 0 && upperOf(t) === 'FROM') {
      fromIdx = j;
      break;
    }
  }

  const cols: string[] = [];
  let depth = 0;
  let segStart = i;
  const flush = (segEnd: number): void => {
    const colName = extractProjectionAlias(tokens, segStart, segEnd);
    if (colName) cols.push(colName);
  };

  for (; i < fromIdx; i += 1) {
    const t = tokens[i]!;
    if (t.type === 'lparen') depth += 1;
    else if (t.type === 'rparen') depth -= 1;
    else if (t.type === 'comma' && depth === 0) {
      flush(i);
      segStart = i + 1;
    }
  }
  flush(fromIdx);
  return cols;
}

function extractProjectionAlias(
  tokens: readonly Token[],
  start: number,
  endExclusive: number,
): string | null {
  // Trim whitespace by walking only token boundaries.
  let lo = start;
  let hi = endExclusive - 1;
  while (lo < endExclusive && (tokens[lo]!.type === 'comma')) lo += 1;
  while (hi >= lo && tokens[hi]!.type === 'comma') hi -= 1;
  if (lo > hi) return null;

  // Shape: ... AS <alias>
  // Look for AS at hi-1 with ident at hi.
  const last = tokens[hi]!;
  if ((last.type === 'ident' || last.type === 'kw') && hi - 1 >= lo) {
    const prev = tokens[hi - 1]!;
    if (upperOf(prev) === 'AS') {
      return last.text.toLowerCase();
    }
  }

  // Shape: <ident> . <colName>  → colName
  if ((last.type === 'ident' || last.type === 'kw') && hi - 2 >= lo) {
    const dot = tokens[hi - 1]!;
    const base = tokens[hi - 2]!;
    // The base must be an ident (alias / table name). If the segment has
    // MORE tokens before that base, we treat it as an expression (skip).
    if (dot.type === 'dot' && base.type === 'ident' && hi - 2 === lo) {
      return last.text.toLowerCase();
    }
  }

  // Shape: <colName> (single token, bare unqualified projection).
  if (lo === hi && last.type === 'ident') {
    return last.text.toLowerCase();
  }

  return null;
}
